import { Component, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Location, NgIf } from '@angular/common';
import { CameraComponent } from '../camera/camera.component';
import { RestService } from '../services/rest.service';
import { MappingTelemetryService } from '../services/mapping-telemetry.service';
import { MatButton } from '@angular/material/button';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CameraConsentComponent } from '../camera/camera-consent/camera-consent.component';
import { CameraInstructionsComponent } from '../camera/camera-instructions/camera-instructions.component';
import { loaderComponent } from '../shared/components/loader/loader.component';
import { SessionService } from '../services/session.service';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { GtmAnalyticsService } from '../services/gtm-analytics.service';
import { LoginService } from '../services/login.service';
import { ErrorReportingService } from '../services/error-reporting.service';

@Component({
  selector: 'app-test',
  standalone: true,
  imports: [
    NgIf,
    CameraComponent,
    MatButton,
    TranslatePipe,
    loaderComponent,
  ],
  templateUrl: './test.component.html',
  styleUrl: './test.component.scss',
})
export class TestComponent {
  @ViewChild('camera') camera: CameraComponent;
  scanId: string;
  isConsentSubmitted = false;
  isInstructionsSubmitted = false;
  openedDialog;

  consent: {
    dataImprovement: boolean;
    dataSharing: boolean;
    tracking?: boolean;
  } | null = null;

  // Consent sentences as shown (keyed by consent_key), forwarded to the server
  // so it can version them. Kept separate from `consent` so it never lands in
  // the scan.consent jsonb.
  consentTexts: { [key: string]: string } | null = null;

  capturedImages = {
    front: null,
  };

  isSending = false;
  isInitializing = false;
  /** Object URL of the captured blob, for the review screen. Built from the
   *  file we already have instead of a second, PNG copy of the same canvas. */
  previewUrl: string | null = null;
  error: string | null = null;
  errorCode: number | null = null;

  constructor(
    private restService: RestService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private sessionService: SessionService,
    private translationService: TranslationService,
    private gtmAnalytics: GtmAnalyticsService,
    private loginService: LoginService,
    private reporter: ErrorReportingService,
    private location: Location,
    private telemetry: MappingTelemetryService
  ) { }

  ngOnInit() {
    this.showConsentDialog();
    // She reads consent + photo prep for a few seconds: load and warm the
    // face landmarker meanwhile so the camera opens with its mesh.
    CameraComponent.preload(this.reporter);

    this.route.queryParams.pipe().subscribe((params) => {
      console.log('test.component.ts: queryParams:', params);
      if (params['scanId']) {
        this.scanId = params['scanId'];
      }
    });
  }

  private showConsentDialog() {
    this.telemetry.step('consent');
    this.telemetry.track('consent_opened');
    this.openedDialog = this.dialog.open(CameraConsentComponent, {
      width: '400px',
      disableClose: true,
      panelClass: 'custom-dialog-class',
    });

    this.openedDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isConsentSubmitted = true;
        const { texts, ...consentFlags } = result;
        this.consent = consentFlags;
        this.consentTexts = texts || null;
        // section 43 + 47: WHICH optional consents she gave, never the text
        this.telemetry.track('consent_completed', {
          dataImprovement: !!consentFlags?.dataImprovement,
          dataSharing: !!consentFlags?.dataSharing,
          tracking: !!consentFlags?.tracking,
        });

        this.showInstructionsDialog();
      } else {
        // Cancelled (e.g. Android back). Leaving the component mounted shows a
        // blank screen (whole template is [hidden] until consent submitted), so
        // go back to where we came from instead.
        console.log('test.component.ts: consent dialog cancelled, going back');
        this.location.back();
      }
    });
  }

  private showInstructionsDialog() {
    this.openedDialog = this.dialog.open(CameraInstructionsComponent, {
      width: '400px',
      disableClose: true,
      panelClass: ['custom-dialog-class', 'prep-dialog'],
    });

    // Initialize camera while instructions are showing
    if (this.camera) {
      this.camera.init();
    }

    this.openedDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isInstructionsSubmitted = true;
        this.gtmAnalytics.trackStartedTest();
        // section 43: this is where the mapping starts, so this is where the
        // active clock starts too (brief 9)
        this.telemetry.beginMapping();
        this.telemetry.step('face_capture');
        this.telemetry.track('photo_prep_viewed');
      } else {
        // Cancelled (e.g. Android back). Same blank-screen trap as consent:
        // nothing renders until instructions submitted, so navigate back.
        console.log('test.component.ts: instructions dialog cancelled, going back');
        this.location.back();
      }
    });
  }

  async retryCamera() {
    if (this.isInitializing) return;

    this.isInitializing = true;

    try {
      if (this.camera) {
        await this.camera.init();
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    } finally {
      this.isInitializing = false;
    }
  }

  isAdmin(): boolean {
    return this.loginService.isAdmin();
  }

  async fakePicture() {
    try {
      this.isInitializing = true;
      const response: Blob = await this.restService.request<Blob>({
        path: `admin/scan/4ce51d0f-15d0-46e2-a278-c60d5785cc5f/downloadImage`,
        method: 'GET',
        responseType: 'blob',
      });

      this.capturedImages.front = { blob: response };
      this.isInitializing = false;
      this.showPreview();
      this.beginUpload();
      this.cdr.detectChanges();

    } catch (error) {
      console.error('Failed to fake picture', error);
      this.reporter.report(error, 'scan');
      this.isInitializing = false;
    }
  }

  removeImage(item: any) {
    this.capturedImages[item] = undefined;
    // Whatever is in flight belongs to the shot she just discarded: its result
    // is no longer hers to wait on (the retake's upload overwrites it server
    // side, on the same scan).
    this.upload = null;
    this.releasePreview();
    this.clearError();
  }

  /** B7: the send/analyse wait failed -> the overlay loader shows the failure
   *  with Retry instead of spinning forever. The captured photo is untouched,
   *  so Retry re-sends exactly what she already took. */
  sendFailed = false;

  clearError() {
    this.error = null;
    this.errorCode = null;
    this.sendFailed = false;
  }

  /** The upload of the shot she is looking at. It is started the moment the
   *  photo exists, so the server analysis runs while she reviews it, and
   *  Proceed then waits only for whatever is left of it (usually nothing).
   *  `photo` is the identity of the shot it belongs to: a retake replaces the
   *  object, and a job for a photo she no longer has is ignored. */
  private upload: { photo: any; done: Promise<any> } | null = null;

  /** Tail of the upload chain. Uploads are serialised because the server keeps
   *  whichever finishes LAST: a retake racing the shot she discarded could
   *  leave the discarded one on the scan. */
  private uploadTail: Promise<any> = Promise.resolve();

  /** Start (or restart) the background upload for the current photo. */
  private beginUpload() {
    const photo = this.capturedImages.front;
    if (!photo || this.upload?.photo === photo) return;
    // A rejection is read by sendToServer(), so swallow it here to keep it off
    // the console as an unhandled rejection.
    const done = this.uploadTail
      .then(() => this.sendPhoto(photo))
      .catch((e) => ({ __error: e }));
    this.uploadTail = done;
    this.upload = { photo, done };
  }

  async sendToServer() {
    if (this.isSending || !this.capturedImages.front) return;

    this.isSending = true;
    this.error = null;
    this.errorCode = null;
    this.sendFailed = false;

    try {
      // Wait on the upload already in flight for THIS photo; start one only if
      // there is none (or the last one failed / belonged to a previous shot).
      const photo = this.capturedImages.front;
      let result =
        this.upload?.photo === photo
          ? await this.upload.done
          : await this.sendPhoto(photo).catch((e) => ({ __error: e }));
      if (result?.__error) {
        // One clean retry on her tap: the background attempt may have failed
        // while the connection was still coming back.
        this.upload = null;
        result = await this.sendPhoto(photo).catch((e) => ({ __error: e }));
      }
      if (result?.__error) throw result.__error;
      await this.router.navigate(['/app/symptoms', this.scanId]);
    } catch (error: any) {
      // Only a failure she is waiting on reaches the screen: a background
      // attempt that failed while she was still deciding is retried silently
      // above, and only the retry's failure is shown.
      this.sendFailed = true;
      this.errorCode = error?.shownCode || 500;
      this.error =
        error?.shownMessage || this.translationService.translate('test.errors.uploadNetworkError');
    } finally {
      this.isSending = false;
    }
  }

  /** Create the scan if needed and upload the given photo (the current one by
   *  default). Throws on failure, carrying the message to show. */
  private async sendPhoto(photo: any = this.capturedImages.front) {
    this.reporter.logEvent('scan.send.start', { hasScanId: !!this.scanId });

    try {
      console.log('sendToServer: creating scan');
      if (!this.scanId) {
        const response: any = await this.restService.request({
          path: 'scan/create',
          method: 'POST',
          body: {
            consent: this.consent,
            consentTexts: this.consentTexts,
            locale: this.translationService.getCurrentLanguage(),
          },
          throwError: true,
        });

        console.log(
          'sendToServer: scan created successfully - scanId:',
          response.scanId
        );
        this.scanId = response.scanId;
        this.reporter.logEvent('scan.create.ok');
      }
    } catch (error: any) {
      console.error('sendToServer: failed to create scan:', error);
      this.reporter.report(error, 'scan');
      error.shownCode = error.error?.code || error.status || 500;

      if (error.status >= 500) {
        error.shownMessage = this.translationService.translate('test.errors.serverError');
      } else if (error.status >= 400) {
        error.shownMessage =
          error.error?.message || this.translationService.translate('test.errors.createScanFailed');
      } else {
        error.shownMessage = this.translationService.translate('test.errors.networkError');
      }
      throw error;
    }

    const info = photo || {};
    const formData = new FormData();
    formData.append('front', info.blob);
    formData.append('scanId', this.scanId);
    // Capture context — recorded per attempt so admins can see every "try":
    // why it was hard (reason), whether it was forced, and client dims.
    formData.append('captureReason', info.reason || '');
    formData.append('forced', info.forced ? 'true' : 'false');
    formData.append('sessionId', this.reporter.getSessionId());
    formData.append('brand', this.reporter.getBrand() || '');
    formData.append('platform', this.reporter.getPlatform());
    formData.append('clientMeta', JSON.stringify(info.meta || {}));
    // Server-side consent gate (V-06): this client always collects camera
    // consent before capture, so it says so (consentGate) and re-sends the
    // decision. The server records it if the scan has none yet (skip path
    // created the scan without consent) and refuses the photo without it.
    formData.append('consentGate', '1');
    if (this.consent) {
      formData.append('consent', JSON.stringify(this.consent));
      formData.append('consentTexts', JSON.stringify(this.consentTexts || {}));
      formData.append('locale', this.translationService.getCurrentLanguage() || '');
    }

    try {
      console.log('sendToServer: uploading images for scanId:', this.scanId);
      this.reporter.logEvent('scan.upload.start');
      // the upload and the analysis behind it are the system working, so they
      // belong in processing time and must not inflate her active time (brief 9)
      this.telemetry.setAssessment(this.scanId);
      this.telemetry.beginProcessing('scan_upload');
      this.telemetry.track('face_capture_completed');

      await this.restService.request({
        path: 'scan/upload-images',
        method: 'POST',
        body: formData,
        throwError: true,
      });

      console.log('sendToServer: images uploaded successfully');
      this.reporter.logEvent('scan.upload.ok');
      this.telemetry.endProcessing('scan_upload', true);
      this.telemetry.track('analysis_started');
    } catch (error: any) {
      console.error('sendToServer: image upload failed:', error);
      this.telemetry.endProcessing('scan_upload', false);
      this.telemetry.countRetry('face_capture');
      this.telemetry.track('face_capture_retake', { status: error?.status });
      this.reporter.report(
        `scan upload failed: status=${error?.status} code=${error?.error?.code || ''}`,
        'scan',
      );
      error.shownCode = error.error?.code || error.status || 500;

      if (error.status === 422) {
        error.shownMessage =
          error.error?.code === 'FACE_TOO_SMALL'
            ? this.translationService.translate('test.errors.faceTooSmall')
            : this.translationService.translate('test.errors.analyzeImageFailed');
      } else if (error.status >= 500) {
        error.shownMessage = this.translationService.translate('test.errors.processImageError');
      } else if (error.status >= 400) {
        error.shownMessage =
          error.error?.message || this.translationService.translate('test.errors.uploadImageFailed');
      } else {
        error.shownMessage = this.translationService.translate('test.errors.uploadNetworkError');
      }
      throw error;
    }
  }

  onImageCaptured() {
    this.showPreview();
    this.beginUpload();
    // Force change detection to ensure the template updates
    this.cdr.detectChanges();
  }

  ngDoCheck() {
    // The camera component is torn down the instant the photo is published, so
    // if its event ever misses, build the preview here rather than show her an
    // empty frame. Runs once per photo: it only fires while the URL is missing.
    if (this.capturedImages.front?.blob && !this.previewUrl) {
      this.showPreview();
      this.beginUpload();
    }
  }

  /** Show the shot back to her from the blob the upload already holds. */
  private showPreview() {
    this.releasePreview();
    const blob = this.capturedImages.front?.blob;
    if (blob) this.previewUrl = URL.createObjectURL(blob);
  }

  private releasePreview() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
  }

  ngOnDestroy() {
    this.releasePreview();
    if (this.openedDialog) {
      this.openedDialog.close();
    }
  }
}
